import { useState, useEffect, useRef, useCallback } from 'react';

// mui
import { Box } from '@mui/material';

// api
import { Feed, Like } from '../../api';

// virtuoso
import { Virtuoso } from 'react-virtuoso';

// client-shared
import { storage } from '@goldblack/client-storage';

// components
import { PostComment, PostShowMediaPlay, PostShowMediaHeader, PostShowMediaBottom } from '../';
import AdsenseAd from '../common/adsense-ad';

// client-shared
import { array } from '@goldblack/client-utils';
import { Message, ContainerScroll, DotLoading } from '@goldblack/client-comps';

// Persisted "distance consumed since last ad" (px), stored at recommend.adDistance in
// goldblack.loise. Survives feed re-entry/reload so ad frequency doesn't restart
// from zero. scrollTop resets to 0 on re-entry, so we persist the consumed distance,
// not an absolute position.
const AD_DISTANCE_PATH = 'recommend.adDistance';

function HomeImages(props) {
    // px of forward scroll between ads. KV-configurable (lo:cf:{env}:client), 6000 fallback.
    const AD_DISTANCE = props.config?.adsRecommendDistance ?? 6000;
    // Runtime ad gate (KV-configurable). Needs the master switch (allowAds) AND the
    // feed-specific switch (allowRecommendAds); either false → no in-feed ad at all.
    const AD_ENABLED = (props.config?.allowAds ?? true) && (props.config?.allowRecommendAds ?? true);
    const [data, setData] = useState({
        loading: false,
        loadmore: true,
        cursor: null,
        posts: [],
        batch: 0,
    });
    const { internal, path } = storage.transience.getValue();
    const [soundExclusiveIndex, setSoundExclusiveIndex] = useState(0);
    const [centerIndex, setCenterIndex] = useState(-1);
    const [centerPoint, setCenterPoint] = useState(new DOMPoint(0, 0));
    const scrollParentRef = useRef(null);
    const fetchingRef = useRef(false);
    const scrollTriggeredRef = useRef(false);
    const [ready, setReady] = useState(false);

    // ad frequency (scroll-distance based)
    const sessionMaxRef = useRef(0); // furthest forward scrollTop this session
    const adDistanceRef = useRef(0); // px consumed since last ad (loaded from localStorage on mount)
    const pendingAdRef = useRef(false); // slot placed, awaiting fill result
    const persistTimerRef = useRef(null); // debounced localStorage write
    const [adIndices, setAdIndices] = useState(() => new Set()); // post indices that have an ad after them

    // Persisting hits LocalStorageState (App subscribes → re-render), so debounce
    // it to scroll-stop instead of writing every scroll frame.
    const persistAdDistance = () => {
        storage.localStorage.setState(AD_DISTANCE_PATH, adDistanceRef.current);
    };
    const persistAdDistanceDebounced = () => {
        if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(persistAdDistance, 500);
    };

    const onAdRendered = (status) => {
        pendingAdRef.current = false;
        if (status === 'filled') {
            adDistanceRef.current = 0;
            persistAdDistance();
        }
        // 'unfilled': keep distance, pending cleared so a later post retries.
    };

    const onClickLike = (post) => {
        // like
        Like(post, (liked, likes) => {
            setData((prev) => {
                const index = prev.posts.findIndex((x) => x.post._id === post._id);
                if (index !== -1) {
                    const newPost = {
                        ...prev.posts[index],
                        post: { ...prev.posts[index].post, liked: liked, likes: likes },
                    };
                    return {
                        ...prev,
                        posts: array.updateInArray(prev.posts, (x) => x.post._id === post._id, newPost),
                    };
                }
            });
        });
    };

    const parseSubKey = (sk) => {
        if (!sk || sk === 'global') return { country: null, gender: null };
        const [c, g] = sk.split(':');
        return { country: c === 'global' ? null : c || null, gender: g === 'all' ? null : g || null };
    };

    const loadMoreRows = () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        const { country, gender } = parseSubKey(props.subKey);
        Feed(
            data.cursor,
            data.batch + 1,
            props.config.recommendLimit,
            props.vecMode ?? 'both',
            country,
            gender,
            (updater) => {
                setData((prev) => {
                    const next = typeof updater === 'function' ? updater(prev) : updater;
                    if ('loading' in next && next.loading === false) fetchingRef.current = false;
                    return next;
                });
            },
        );
    };

    // Home stays mounted under profile/dialog screens with display:none. A hidden scroll parent
    // has zero height, so Virtuoso is not rendered while hidden (iOS fires a scroll on the hidden
    // container → Virtuoso stack overflow, LOISE-CLIENT-8). Remember scrollTop to restore it.
    const lastTopRef = useRef(0);
    useEffect(() => {
        if (props.hidden) return;
        const top = lastTopRef.current;
        if (!top) return;
        // Virtuoso remounts with estimated sizes; restore after its first layout.
        const id = requestAnimationFrame(() => {
            if (scrollParentRef.current) scrollParentRef.current.scrollTop = top;
        });
        return () => cancelAnimationFrame(id);
    }, [props.hidden]);

    const onScroll = (e) => {
        if (props.hidden) return;
        lastTopRef.current = e.currentTarget.scrollTop;
        scrollTriggeredRef.current = true;
        const current = calcCenterIndex();
        calcSoundExclusiveIndex(internal.mute, current);
        accumulateAdDistance(current);
    };

    // Accumulate only forward-scroll distance; place an ad after the current center
    // post once the consumed distance crosses AD_DISTANCE. Scrolling back adds nothing.
    const accumulateAdDistance = (currentIndex) => {
        if (!AD_ENABLED) return;
        const scrollTop = Math.round(scrollParentRef.current?.scrollTop ?? 0);
        if (scrollTop <= sessionMaxRef.current) return;
        adDistanceRef.current += scrollTop - sessionMaxRef.current;
        sessionMaxRef.current = scrollTop;
        persistAdDistanceDebounced();
        if (adDistanceRef.current < AD_DISTANCE || pendingAdRef.current) return;
        if (currentIndex < 0 || adIndices.has(currentIndex)) return;
        pendingAdRef.current = true;
        setAdIndices((prev) => new Set(prev).add(currentIndex));
    };

    const calcSoundExclusiveIndex = (muted, currentIndex = centerIndex) => {
        if (!muted && path.second !== '/profile' && !internal.dialog) {
            setSoundExclusiveIndex(currentIndex);
        }
    };

    const feedMute = (index) => {
        return !(index === soundExclusiveIndex && !internal.mute && path.second !== '/profile' && !internal.dialog);
    };
    const feedMuteRef = useRef(feedMute);
    feedMuteRef.current = feedMute;
    const adIndicesRef = useRef(adIndices);
    adIndicesRef.current = adIndices;

    const itemContent = useCallback(
        (index, post) => (
            <>
                <Box sx={{ minHeight: '300px' }}>
                    <Box sx={{ position: 'relative' }}>
                        <PostShowMediaPlay
                            {...post}
                            {...props}
                            muted={feedMuteRef.current(index)}
                            dialog={false}
                            onMuteChange={onMuteChange}
                        />
                        <PostShowMediaHeader {...post} {...props} />
                        <PostShowMediaBottom
                            {...post}
                            {...props}
                            onClickLike={() => onClickLike(post.post)}
                            onClickComment={() => props.onClickComment(post)}
                        />
                    </Box>
                    <PostComment {...post} {...props} />
                </Box>
                {AD_ENABLED && adIndicesRef.current.has(index) && (
                    <AdsenseAd
                        slotId={import.meta.env.VITE_ADSENSE_SLOT_FEED}
                        style={{ width: '100%', minHeight: '250px' }}
                        onRendered={onAdRendered}
                    />
                )}
            </>
        ),
        [],
    );

    const onMuteChange = (muted) => {
        const current = centerIndex === -1 ? calcCenterIndex() : centerIndex;
        calcSoundExclusiveIndex(muted, current);
    };

    const calcCenterIndex = () => {
        const getCenterItem = (center) => {
            const contain = (rect, pt) => {
                return rect.x <= pt.x && pt.x <= rect.x + rect.width && rect.y <= pt.y && pt.y <= rect.y + rect.height;
            };

            const items = document.querySelectorAll('[data-index]');
            for (let item of items) {
                let rect = item.getBoundingClientRect();
                if (contain(rect, center)) {
                    return parseInt(item.dataset.index);
                }
            }
            return -1;
        };

        let selected = getCenterItem(centerPoint);
        setCenterIndex(selected);
        return selected;
    };

    const calcCenterPosition = () => {
        let scroll = scrollParentRef.current;
        let rect = scroll.getBoundingClientRect();
        return new DOMPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
    };

    const handleResize = () => {
        setCenterPoint(calcCenterPosition());
    };

    useEffect(() => {
        if (scrollTriggeredRef.current && data.posts.length > 0) {
            scrollTriggeredRef.current = false;
            props.onFirstBatch?.();
        }
    }, [data.posts.length]);

    useEffect(() => {
        const saved = storage.localStorage.getValue(AD_DISTANCE_PATH);
        if (Number.isFinite(saved)) adDistanceRef.current = saved;
        setCenterPoint(calcCenterPosition());
        loadMoreRows();
        if (scrollParentRef.current) {
            setReady(true);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
            persistAdDistance();
        };
    }, []);

    const isFirstRender = useRef(true);
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        fetchingRef.current = false;
        scrollTriggeredRef.current = false;
        // new feed: scrollTop resets to 0; clear session ad anchors but keep distance.
        sessionMaxRef.current = 0;
        pendingAdRef.current = false;
        setAdIndices(new Set());
        setData({ loading: false, loadmore: true, cursor: null, posts: [], batch: 0 });
        setReloadKey((k) => k + 1);
    }, [props.feedKey]);

    useEffect(() => {
        if (reloadKey === 0) return;
        loadMoreRows();
    }, [reloadKey]);

    return (
        <ContainerScroll ref={scrollParentRef} onScroll={onScroll}>
            {data.loading && data.posts.length === 0 ? (
                <DotLoading variant='pulse' sx={{ height: '100%' }} />
            ) : ready && !props.hidden ? (
                data.posts.length > 0 ? (
                    <Virtuoso
                        data={data.posts}
                        overscan={1000}
                        increaseViewportBy={{ top: 1000, bottom: 1000 }}
                        itemContent={itemContent}
                        customScrollParent={scrollParentRef.current}
                        endReached={(index) => {
                            if (data.loadmore && !fetchingRef.current) {
                                loadMoreRows();
                            }
                        }}
                    />
                ) : (
                    <Message variant='m_body1' message={'No Data'} />
                )
            ) : null}
        </ContainerScroll>
    );
}

export { HomeImages };
